import { mapRecordsFromServer } from 'core/mappers';
import { partfinitiClient } from 'core/services';
import { GetManyResponse } from 'core/types';
import {
  ICancelConsumerJobsRequestPayload,
  ICancelConsumerJobsResult,
  IConsumerJob,
  IConsumerRestartRequestPayload,
  IConsumerRestartResult,
  IConsumerRestartTarget,
} from 'dashboard/types';

/**
 * Environments this deployment is configured to restart. Returns an empty list on
 * any deployment that has not opted in — prod ships that way — which is what hides
 * the section rather than rendering an empty one.
 */
export const getConsumerRestartTargets = async (
  signal?: AbortSignal
): Promise<GetManyResponse<IConsumerRestartTarget>> => {
  const { data } = await partfinitiClient.get('/admin/consumer-restart/targets', signal);

  return mapRecordsFromServer<IConsumerRestartTarget>(data?.data?.targets ?? []);
};

/**
 * The BE answers with `success: false` and a human message for a restart it refused
 * or that came back with no consumers running, so the result is unwrapped here and
 * branched on in the hook rather than left to the generic error handler.
 */
export const restartConsumers = async (
  requestPayload: IConsumerRestartRequestPayload
): Promise<IConsumerRestartResult> => {
  const response = await partfinitiClient.postForm(
    '/admin/consumer-restart/restart',
    requestPayload
  );

  return response?.data as IConsumerRestartResult;
};

/**
 * Queued and running CLI jobs, so the page can show what a restart would disturb.
 * The list is box-wide, not per-env: every dev instance shares one database and
 * cli_job has no env column, so rows carry a derived `env` only where the BE could
 * establish one.
 */
export const getConsumerJobs = async (
  signal?: AbortSignal
): Promise<GetManyResponse<IConsumerJob>> => {
  const { data } = await partfinitiClient.get('/admin/consumer-restart/jobs', signal);

  return mapRecordsFromServer<IConsumerJob>(data?.data?.jobs ?? []);
};

/**
 * Cancel pending CLI jobs. The BE only touches queued and running rows and reports
 * how many it skipped, so a stale selection degrades into a count rather than an
 * error.
 */
export const cancelConsumerJobs = async (
  requestPayload: ICancelConsumerJobsRequestPayload
): Promise<ICancelConsumerJobsResult> => {
  const response = await partfinitiClient.postForm('/admin/consumer-restart/cancel-jobs', {
    jobIds: requestPayload.jobIds.join(','),
  });

  return response?.data as ICancelConsumerJobsResult;
};
